<script setup lang="ts">
import '../../assets/hljs-themes/stackoverflow-dark.css';
import '../../assets/hljs-themes/stackoverflow-light.css';

import hljs from 'highlight.js';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { ClipboardCopyIcon } from 'lucide-vue-next';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { HIGHLIGHT_JS_PROGRAMMING_LANGUAGES } from '@/constants/HighlightJsProgrammingLanguages.ts';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import Loader from '@/components/loader/Loader.vue';
import PromptStatusBadge from '@/components/output/PromptStatusBadge.vue';
import copy from 'copy-to-clipboard';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PromptService } from '@/services/PromptService.ts';
import { VariableService } from '@/services/VariableService.ts';
import { ROUTES } from '@/router.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import PromptEditButton from '@/components/output/PromptEditButton.vue';

const props = defineProps<{
    sessionId: string;
    profileConfig: ProfileConfig;
    stepConfig: StepConfig;
    promptConfig: PromptConfig;
    promptIndex: number;
    streamId: string;
    content: string;
    selectedNavigationIndex: number;
    variableChainHash: string;
    isPromptSlow: boolean;
}>();

const PROGRAMMING_LANGUAGE_VARIABLE_ID = '98d8feff-4acf-4f05-92e7-e07b04250dc1';

const settingsStore = useSettingsStore();
const sessionStore = useSessionStore();

const codeOutputElement = ref<HTMLDivElement | null>(null);
const formattedContent = ref('');

const cleanCodeString = (str: string) => {
    const cleaned = str.replace(/^```[a-z]*\n|```$/g, ''); // Remove outer brackets and language specifier
    return cleaned.trim();
};

const formatCode = async () => {
    await nextTick();

    if (codeOutputElement.value) {
        const result = hljs.highlight(cleanCodeString(props.content), {
            language: getHighlightJsLanguage(),
        });

        formattedContent.value = result.value;
    }
};

const getHighlightJsLanguage = (): string => {
    const programmingLanguage = VariableService.getDefaultVariableValue(
        PROGRAMMING_LANGUAGE_VARIABLE_ID,
    );

    if (programmingLanguage === undefined || typeof programmingLanguage !== 'string') {
        return 'python';
    }

    if (HIGHLIGHT_JS_PROGRAMMING_LANGUAGES[programmingLanguage] !== undefined) {
        return HIGHLIGHT_JS_PROGRAMMING_LANGUAGES[programmingLanguage];
    }

    return 'python';
};

/* region Computed */
const promptStatus = computed(() => {
    return PromptService.getPromptStatus(
        props.promptConfig.id,
        props.sessionId,
        props.variableChainHash,
    );
});
/* endregion Computed */

/* region Lifecycle */
watch(
    () => props.content,
    async () => {
        await formatCode();
    },
);

onMounted(async () => {
    await formatCode();
});
/* region Lifecycle */
</script>

<template>
    <Card>
        <CardHeader class="px-4 py-2 pe-2 text-lg">
            <div class="flex items-center justify-between text-base">
                {{ promptConfig.name }}
                <div class="flex items-center gap-x-1">
                    <PromptStatusBadge
                        :prompt-config-id="promptConfig.id"
                        :session-id="sessionId"
                        :variable-chain-hash="variableChainHash"
                    />
                    <PromptEditButton
                        v-if="!sessionStore.isInsideWebView"
                        :profile-config-id="profileConfig.id"
                        :step-config-id="stepConfig.id"
                        :prompt-config-id="promptConfig.id"
                    />
                </div>
            </div>
        </CardHeader>
        <CardContent class="relative p-0 dark:bg-zinc-900/60">
            <div
                v-if="props.content !== ''"
                :class="{
                    'theme-stackoverflow-dark': settingsStore.darkModeEnabled,
                    'theme-stackoverflow-light': !settingsStore.darkModeEnabled,
                }"
            >
                <TooltipProvider :delay-duration="150">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Badge
                                variant="secondary"
                                class="absolute right-1.5 top-1.5 rounded-sm bg-transparent text-xs"
                                @click="copy(props.content)"
                                ><ClipboardCopyIcon class="h-4 w-4"
                            /></Badge>
                        </TooltipTrigger>
                        <TooltipContent :side="'left'">
                            <p>Copy to clipboard</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <pre><code ref="codeOutputElement" class="code-output hljs" v-html="formattedContent"></code></pre>
            </div>
            <div
                v-else-if="promptStatus.status === 'error'"
                class="flex items-center justify-center p-5"
            >
                Failed to retrieve output, please try again.
            </div>
            <div v-else class="flex flex-col items-center justify-center p-5">
                <Loader :width="24" :height="24" />
                <div
                    v-if="isPromptSlow && promptStatus.status !== 'streaming'"
                    class="mt-4 max-w-md text-center text-sm"
                >
                    <template v-if="!sessionStore.isInsideWebView">
                        The selected AI model (<strong>{{ promptConfig.modelId }}</strong
                        >) is currently taking a long time to respond. We recommend temporarily
                        <RouterLink
                            v-if="promptConfig.editable"
                            :to="{
                                name: ROUTES.editUpdate,
                                params: { profileConfigId: profileConfig.id },
                            }"
                            class="text-blue-600 hover:underline"
                            >switching</RouterLink
                        >
                        <RouterLink
                            v-else
                            :to="{
                                name: ROUTES.editIndex,
                            }"
                            class="text-blue-600 hover:underline"
                            >switching</RouterLink
                        >
                        to another model.
                    </template>
                    <template v-else>
                        The selected AI model (<strong>{{ promptConfig.modelId }}</strong
                        >) is currently taking a long time to respond. We recommend temporarily
                        switching to another model.
                    </template>
                </div>
            </div>
        </CardContent>
    </Card>
</template>

<style scoped>
.code-output {
    @apply overflow-hidden whitespace-pre-wrap bg-transparent;
    user-select: text !important;
}

.code-output * {
    user-select: text !important;
}
</style>
